import React from "react";
import { StyleSheet, View } from "react-native";
import { colors, layout, shadows } from "@/constants";
import Interactive from "./Interactive";
import Text from "./Text";
import Icon, { IconName, IconSize } from "./Icon";

/**
 * Visual variant.
 *
 * - `primary`: brand-tinted fill, default for affirmative actions.
 * - `secondary`: muted neutral fill, default for Cancel / dismiss.
 * - `destructive`: tonal error fill (red label on pale red), for delete /
 *   remove — the destructive analog of `primary`.
 */
type Variant = "primary" | "secondary" | "destructive";

interface TextButtonProps {
  /** The text to display inside the button */
  buttonText: string;
  /** Width override. Default is content-sized with a sensible minimum. */
  width?: number;
  /** Height override. Default 48. */
  height?: number;
  /** Visual variant. Defaults to "primary". */
  variant?: Variant;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Accessibility label for screen readers */
  accessibilityLabel?: string;
  /** Callback function triggered when the button is pressed */
  onPress: () => void;
  /** Optional Icon to display before text */
  icon?: IconName;
  /** Size of the icon from typography.fontSizes (defaults to medium) */
  iconSize?: IconSize;
}

/** Minimum content width — a one-word button doesn't shrink below this. */
const MIN_WIDTH = 100;
const DEFAULT_HEIGHT = 48;

interface VariantStyle {
  backgroundColor: string;
  textColor: string;
}

const VARIANTS: Record<Variant, VariantStyle> = {
  primary: {
    backgroundColor: colors.primaryContainer,
    textColor: colors.onPrimaryContainer
  },
  secondary: {
    backgroundColor: colors.surfaceVariant,
    textColor: colors.onSurface
  },
  destructive: {
    backgroundColor: colors.errorContainer,
    textColor: colors.onErrorContainer
  }
};

/**
 * A reusable rectangular text button component with consistent styling.
 *
 * Default sizing is content-driven with a sensible minimum width. Callers
 * should rarely pass explicit width/height — fixed sizes from the call site
 * have caused inconsistencies across modals.
 *
 * @param props - The component props
 * @returns A standardized button with text, built on the Interactive primitive
 */
export default function TextButton({
  buttonText,
  width,
  height = DEFAULT_HEIGHT,
  variant = "primary",
  disabled = false,
  accessibilityLabel,
  onPress,
  icon,
  iconSize = "medium"
}: TextButtonProps) {
  const { backgroundColor, textColor } = VARIANTS[variant];

  return (
    <Interactive
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.button,
        { height, backgroundColor },
        width !== undefined ? { width } : { minWidth: MIN_WIDTH }
      ]}
    >
      <View style={styles.content}>
        {icon && (
          <Icon name={icon} size={iconSize} style={{ color: textColor }} />
        )}
        <Text variant="medium" size="large" style={{ color: textColor }}>
          {buttonText}
        </Text>
      </View>
    </Interactive>
  );
}

const styles = StyleSheet.create({
  button: {
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: layout.space.lg,
    borderRadius: layout.borderRadius.large,
    ...shadows.raised
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.sm
  }
});
